import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Award, Star, Trophy, Crown, TrendingUp, Calendar, CheckCircle,
  Lock, ChevronRight, Target, BookOpen, Users, Heart, Brain,
  Clock, FileText, Zap, ArrowUp
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverCareer = () => {
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [levels, setLevels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const getToken = () => localStorage.getItem('observer_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = getToken();
      
      const [profileRes, progressRes, levelsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/career/profile`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/observer/career/progress`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/observer/career/levels`, { params: { token } })
      ]);
      
      setProfile(profileRes.data);
      setProgress(progressRes.data);
      setLevels(levelsRes.data);
    } catch (error) {
      console.error('Error loading career data:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestPromotion = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${BACKEND_URL}/api/observer/career/request-promotion`, null, {
        params: { token }
      });
      
      if (response.data.success) {
        alert('Promotion request submitted! Your principal will review it.');
        loadData();
      } else {
        alert(`Cannot request promotion: ${response.data.missing_requirements?.join(', ')}`);
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Error requesting promotion');
    }
  };

  const getLevelIcon = (level) => {
    const icons = {
      L1: Star,
      L2: Award,
      L3: Trophy,
      L4: Crown
    };
    return icons[level] || Star;
  };

  const getLevelColor = (level) => {
    const colors = {
      L1: '#6b7280',
      L2: '#3b82f6',
      L3: '#8b5cf6',
      L4: '#f59e0b'
    };
    return colors[level] || '#6b7280';
  };

  const getSkillIcon = (category) => {
    const icons = {
      communication: Users,
      emotional: Heart,
      safety: Zap,
      relationship: Users,
      knowledge: Brain,
      leadership: Crown,
      administrative: FileText
    };
    return icons[category] || Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  const currentLevel = profile?.career?.current_level || 'L1';
  const levelInfo = profile?.career?.level_info || {};
  const nextLevelInfo = profile?.career?.next_level_info;
  const eligibility = profile?.next_level_eligibility;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100" data-testid="observer-career-page">
      <ObserverNav />
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-purple-500" />
              Career Progression
            </h1>
            <p className="text-gray-600">Track your growth from L1 to L4</p>
          </div>

          {/* Current Level Card */}
          <Card className="mb-6 shadow-xl overflow-hidden">
            <div 
              className="p-6 text-white"
              style={{ background: `linear-gradient(135deg, ${getLevelColor(currentLevel)}, ${getLevelColor(currentLevel)}99)` }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                    {React.createElement(getLevelIcon(currentLevel), { className: "w-10 h-10" })}
                  </div>
                  <div>
                    <p className="text-sm opacity-80">Current Level</p>
                    <h2 className="text-3xl font-bold">{levelInfo.title}</h2>
                    <p className="text-sm opacity-80">{levelInfo.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-80">Career Points</p>
                  <p className="text-4xl font-bold">{profile?.total_points || 0}</p>
                </div>
              </div>
            </div>
            
            {/* Stats */}
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{profile?.stats?.total_sessions || 0}</p>
                  <p className="text-xs text-gray-500">Total Sessions</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Clock className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{profile?.stats?.tenure_months || 0}</p>
                  <p className="text-xs text-gray-500">Months Experience</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Star className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{profile?.stats?.average_rating?.toFixed(1) || '—'}</p>
                  <p className="text-xs text-gray-500">Avg Rating</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Zap className="w-6 h-6 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{profile?.stats?.flags_raised || 0}</p>
                  <p className="text-xs text-gray-500">Flags Raised</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {['overview', 'progress', 'skills', 'certifications'].map(tab => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab)}
                className={activeTab === tab ? 'bg-purple-500 hover:bg-purple-600' : ''}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Current Benefits */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-purple-500" />
                    Current Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>₹{levelInfo.benefits?.base_rate_per_session || 50} per session</span>
                    </li>
                    <li className="flex items-center gap-3">
                      {levelInfo.benefits?.bonus_eligible ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Lock className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={!levelInfo.benefits?.bonus_eligible ? 'text-gray-400' : ''}>
                        Bonus Eligible
                      </span>
                    </li>
                    <li className="flex items-center gap-3">
                      {levelInfo.benefits?.priority_assignments ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Lock className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={!levelInfo.benefits?.priority_assignments ? 'text-gray-400' : ''}>
                        Priority Assignments
                      </span>
                    </li>
                    <li className="flex items-center gap-3">
                      {levelInfo.benefits?.can_mentor ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Lock className="w-5 h-5 text-gray-400" />
                      )}
                      <span className={!levelInfo.benefits?.can_mentor ? 'text-gray-400' : ''}>
                        Can Mentor Others
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Next Level */}
              {nextLevelInfo && (
                <Card className="border-2 border-purple-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowUp className="w-5 h-5 text-purple-500" />
                      Next Level: {nextLevelInfo.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{nextLevelInfo.description}</p>
                    
                    {eligibility && (
                      <div className="space-y-2 mb-4">
                        {eligibility.eligible ? (
                          <div className="p-3 bg-green-50 rounded-lg text-green-700">
                            <CheckCircle className="w-5 h-5 inline mr-2" />
                            You&apos;re eligible for promotion!
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-700">Requirements to unlock:</p>
                            {eligibility.missing_requirements?.map((req, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-orange-600">
                                <Lock className="w-4 h-4" />
                                {req}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                    
                    <Button 
                      onClick={requestPromotion}
                      disabled={!eligibility?.eligible}
                      className="w-full bg-purple-500 hover:bg-purple-600"
                    >
                      {eligibility?.eligible ? 'Request Promotion' : 'Not Yet Eligible'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && progress && (
            <Card>
              <CardHeader>
                <CardTitle>Career Progression Path</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {progress.progress?.map((level, idx) => {
                    const Icon = getLevelIcon(level.level_id);
                    const isCompleted = level.status === 'completed';
                    const isCurrent = level.status === 'current';
                    
                    return (
                      <div key={level.level_id} className="relative">
                        {/* Connector line */}
                        {idx < progress.progress.length - 1 && (
                          <div className={`absolute left-6 top-14 w-0.5 h-full ${
                            isCompleted ? 'bg-green-500' : 'bg-gray-200'
                          }`} />
                        )}
                        
                        <div className={`flex items-start gap-4 p-4 rounded-xl ${
                          isCurrent ? 'bg-purple-50 border-2 border-purple-300' :
                          isCompleted ? 'bg-green-50' : 'bg-gray-50'
                        }`}>
                          <div 
                            className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${
                              isCompleted ? 'bg-green-500 text-white' :
                              isCurrent ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-600'
                            }`}
                          >
                            {isCompleted ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-bold text-gray-900">{level.title}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                isCompleted ? 'bg-green-100 text-green-700' :
                                isCurrent ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-600'
                              }`}>
                                {level.status === 'current' ? 'Current' :
                                 level.status === 'completed' ? 'Completed' : 'Locked'}
                              </span>
                            </div>
                            
                            {/* Progress bar */}
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                              <div 
                                className={`h-full transition-all ${
                                  isCompleted ? 'bg-green-500' :
                                  isCurrent ? 'bg-purple-500' : 'bg-gray-400'
                                }`}
                                style={{ width: `${level.completion_percentage}%` }}
                              />
                            </div>
                            
                            <p className="text-sm text-gray-500">
                              {level.completion_percentage}% complete
                            </p>
                            
                            {/* Requirements summary */}
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {level.requirements.min_sessions} sessions
                              </span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {level.requirements.min_months} months
                              </span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                {level.requirements.min_rating}+ rating
                              </span>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Rate</p>
                            <p className="font-bold text-green-600">
                              ₹{level.benefits.base_rate_per_session}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Skills Tab */}
          {activeTab === 'skills' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Your Skills */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    Your Skills
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile?.skills?.length > 0 ? (
                    <div className="space-y-3">
                      {profile.skills.map((skill, idx) => {
                        const Icon = getSkillIcon(skill.details?.category);
                        return (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                            <Icon className="w-5 h-5 text-yellow-600" />
                            <div className="flex-1">
                              <p className="font-medium">{skill.name}</p>
                              <p className="text-xs text-gray-500">{skill.details?.description}</p>
                            </div>
                            {skill.verified ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                Pending
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No skills added yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* All Skills */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    Available Skills
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {levels?.skills && Object.entries(levels.skills).map(([skillId, skill]) => {
                      const hasSkill = profile?.skills?.some(s => s.skill_id === skillId);
                      const Icon = getSkillIcon(skill.category);
                      
                      return (
                        <div key={skillId} className={`flex items-center gap-3 p-3 rounded-lg ${
                          hasSkill ? 'bg-green-50' : 'bg-gray-50'
                        }`}>
                          <Icon className={`w-5 h-5 ${hasSkill ? 'text-green-600' : 'text-gray-400'}`} />
                          <div className="flex-1">
                            <p className={`font-medium ${hasSkill ? 'text-green-800' : 'text-gray-700'}`}>
                              {skill.name}
                            </p>
                            <p className="text-xs text-gray-500">{skill.description}</p>
                          </div>
                          {hasSkill && <CheckCircle className="w-5 h-5 text-green-500" />}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Certifications Tab */}
          {activeTab === 'certifications' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Your Certifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-500" />
                    Your Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {profile?.certifications?.length > 0 ? (
                    <div className="space-y-3">
                      {profile.certifications.map((cert, idx) => (
                        <div key={idx} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-purple-800">{cert.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              cert.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {cert.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{cert.details?.description}</p>
                          {cert.completed_at && (
                            <p className="text-xs text-gray-400 mt-2">
                              Completed: {new Date(cert.completed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No certifications yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* All Certifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    Available Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {levels?.certifications && Object.entries(levels.certifications).map(([certId, cert]) => {
                      const hasCert = profile?.certifications?.some(c => c.certification_id === certId && c.status === 'completed');
                      
                      return (
                        <div key={certId} className={`p-4 rounded-lg ${
                          hasCert ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className={`font-bold ${hasCert ? 'text-green-800' : 'text-gray-700'}`}>
                              {cert.name}
                            </h4>
                            {hasCert && <CheckCircle className="w-5 h-5 text-green-500" />}
                          </div>
                          <p className="text-sm text-gray-600">{cert.description}</p>
                          <div className="mt-2 flex gap-2">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              {cert.duration_hours} hours
                            </span>
                            {cert.required_for?.length > 0 && (
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded">
                                Required for: {cert.required_for.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverCareer;
